import type { ComparisonConfig } from './ComparisonPage';

/* ========================================================================== */
/*  HotelStack.io vs IDeaS (by SAS)                                          */
/* ========================================================================== */

const HOTELSTACK_VS_IDEAS: ComparisonConfig = {
  slug: 'hotelstack-vs-ideas',
  competitorName: 'IDeaS (SAS)',
  heroTitle: 'HotelStack.io vs IDeaS Revenue Solutions',
  heroSubtitle: 'IDeaS by SAS is a revenue management system focused on AI-powered pricing optimisation. HotelStack.io delivers the same AI pricing intelligence plus 24 additional AI features, a full 40+ module PMS, and everything else you need — at a fraction of the cost.',
  overviewComparison: [
    { label: 'Platform Type', hotelstack: 'All-in-One HMS (40+ Modules)', competitor: 'Revenue Management Only' },
    { label: 'AI Features', hotelstack: '25 AI Features (Full Suite)', competitor: '3-5 (Revenue Focused)' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: '$500-2,000+/mo' },
    { label: 'Built-In PMS', hotelstack: 'Full PMS Included', competitor: 'Not Included (Requires PMS)' },
    { label: 'Implementation', hotelstack: 'Free — 48hr Onboarding', competitor: '3-6 Months + Consulting Fees' },
    { label: 'Target Segment', hotelstack: 'SMB to Enterprise', competitor: 'Enterprise Only' },
  ],
  featureTable: [
    {
      category: 'AI Revenue Management',
      features: [
        { name: 'AI Dynamic Pricing Engine', hotelstack: true, competitor: true },
        { name: 'Demand Forecasting (AI)', hotelstack: true, competitor: true },
        { name: 'Competitor Rate Intelligence', hotelstack: true, competitor: true },
        { name: 'Autonomous Rate Management', hotelstack: true, competitor: 'Limited' },
        { name: 'Booking Pattern Discovery', hotelstack: true, competitor: 'Basic' },
        { name: 'What-If Revenue Simulator', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'AI Guest Experience',
      features: [
        { name: 'AI Guest Concierge Chatbot', hotelstack: true, competitor: false },
        { name: 'Voice AI Concierge', hotelstack: true, competitor: false },
        { name: 'AI Review Response Generator', hotelstack: true, competitor: false },
        { name: 'Smart Upsell Recommendations', hotelstack: true, competitor: false },
        { name: 'Guest Sentiment Analysis', hotelstack: true, competitor: false },
        { name: 'AI Guest Profiling 360', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'AI Operations',
      features: [
        { name: 'Automated Night Audit', hotelstack: true, competitor: false },
        { name: 'Smart Housekeeping Scheduler', hotelstack: true, competitor: false },
        { name: 'AI Staff Scheduling', hotelstack: true, competitor: false },
        { name: 'Predictive Maintenance', hotelstack: true, competitor: false },
        { name: 'AI Energy Optimisation', hotelstack: true, competitor: false },
        { name: 'AI Operations Agent', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Core PMS & Operations',
      features: [
        { name: 'Property Management System', hotelstack: true, competitor: false },
        { name: 'Channel Manager', hotelstack: true, competitor: false },
        { name: 'Booking Engine', hotelstack: true, competitor: false },
        { name: 'F&B / POS Management', hotelstack: true, competitor: false },
        { name: 'Housekeeping Module', hotelstack: true, competitor: false },
        { name: 'Guest CRM & Loyalty', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Enterprise & Chain',
      features: [
        { name: 'Digital Twin Dashboard', hotelstack: true, competitor: false },
        { name: 'Cross-Property Intelligence', hotelstack: true, competitor: 'Limited' },
        { name: 'AI Travel Agent MCP', hotelstack: true, competitor: false },
        { name: 'AI Compliance Automation', hotelstack: true, competitor: false },
        { name: 'Platform Anomaly Detection', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$500+',
    hotelstackNote: 'All 40+ modules with 25 AI features included. Revenue management + full PMS + guest CRM + F&B. Free onboarding.',
    competitorNote: 'Revenue management only. Requires separate PMS, channel manager, guest CRM. Enterprise pricing with consulting fees.',
  },
  advantages: [
    'Complete 40+ module HMS with AI — not just revenue management. One platform for everything.',
    'Roughly 90% lower starting price: $49/mo vs $500-2,000+/mo for IDeaS',
    '25 AI features across revenue, guest, operations, maintenance, and compliance — vs 3-5 revenue-only AI features',
    'No separate PMS, channel manager, or guest CRM needed — everything built in',
    'Free 48-hour onboarding vs 3-6 month implementation with consulting fees',
    'AI guest concierge, voice AI, and sentiment analysis included — zero in IDeaS',
    'Predictive maintenance, energy optimisation, and digital twin — not available in IDeaS',
    'Accessible to SMB hotels, not just enterprise chains',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'IDeaS is a market leader in AI revenue management — but it is only revenue management. HotelStack.io delivers the same AI pricing intelligence plus 20 additional AI features, a full 40+ module PMS, and everything else a hotel needs. For the price of IDeaS alone, you get the entire HotelStack platform with more AI capabilities.',
    bestFor: [
      'Hotels wanting AI pricing AND a full PMS in one platform',
      'Properties that cannot justify $500+/mo for revenue management alone',
      'Hotels needing AI beyond revenue — guest experience, operations, maintenance',
      'SMB and mid-scale properties not served by enterprise-only IDeaS',
      'Chains wanting cross-department AI, not just cross-property revenue optimisation',
    ],
  },
  faqItems: [
    { question: 'Is HotelStack AI pricing as good as IDeaS?', answer: 'HotelStack AI dynamic pricing uses ML models that adjust rates every 15 minutes based on demand, competitor rates, events, and weather. While IDeaS has decades of revenue management data, HotelStack combines comparable pricing intelligence with 20 additional AI features across all hotel departments — at a fraction of the cost.' },
    { question: 'Do I still need IDeaS if I use HotelStack?', answer: 'No. HotelStack includes AI dynamic pricing, demand forecasting, competitor rate monitoring, autonomous rate management, and booking pattern discovery — covering all the core capabilities of IDeaS. Plus you get AI guest concierge, voice AI, sentiment analysis, predictive maintenance, and 15 more AI features.' },
    { question: 'Does HotelStack include a PMS? IDeaS does not.', answer: 'Yes. HotelStack is a complete 40+ module hotel management platform. IDeaS is revenue-management-only and requires you to separately purchase a PMS like Opera, Mews, or Cloudbeds. With HotelStack, PMS + AI revenue management + everything else is included from $49/month.' },
    { question: 'Can small hotels afford AI revenue management?', answer: 'With IDeaS, enterprise pricing ($500-2,000+/month) puts AI revenue management out of reach for most hotels. HotelStack democratises AI for hotels of all sizes — AI dynamic pricing is included in Professional plans starting at $99/month, with the full 25-feature AI suite in Enterprise plans.' },
  ],
};

/* ========================================================================== */
/*  HotelStack.io vs Duetto                                                   */
/* ========================================================================== */

const HOTELSTACK_VS_DUETTO: ComparisonConfig = {
  slug: 'hotelstack-vs-duetto',
  competitorName: 'Duetto',
  heroTitle: 'HotelStack.io vs Duetto',
  heroSubtitle: 'Duetto is a cloud-based revenue management platform known for open pricing and demand-based rate optimisation. HotelStack.io offers equivalent AI pricing capabilities plus 20 more AI features, a complete 40+ module PMS, and dramatically lower pricing.',
  overviewComparison: [
    { label: 'Platform Type', hotelstack: 'All-in-One HMS (40+ Modules)', competitor: 'Revenue Management Platform' },
    { label: 'AI Features', hotelstack: '25 AI Features (Full Suite)', competitor: '4-6 (Revenue Focused)' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: '$300-1,500+/mo' },
    { label: 'Built-In PMS', hotelstack: 'Full PMS Included', competitor: 'Not Included' },
    { label: 'Open Pricing', hotelstack: 'Dynamic Pricing + Open API', competitor: 'Open Pricing Model' },
    { label: 'Implementation', hotelstack: 'Free — 48hr Onboarding', competitor: 'Weeks + Integration Work' },
  ],
  featureTable: [
    {
      category: 'AI Revenue Management',
      features: [
        { name: 'AI Dynamic Pricing Engine', hotelstack: true, competitor: true },
        { name: 'Demand Forecasting (AI)', hotelstack: true, competitor: true },
        { name: 'Open Pricing / Flexible Rates', hotelstack: true, competitor: true },
        { name: 'Competitor Rate Intelligence', hotelstack: true, competitor: true },
        { name: 'Autonomous Rate Management', hotelstack: true, competitor: 'Limited' },
        { name: 'What-If Revenue Simulator', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'AI Guest Experience',
      features: [
        { name: 'AI Guest Concierge Chatbot', hotelstack: true, competitor: false },
        { name: 'Voice AI Concierge', hotelstack: true, competitor: false },
        { name: 'AI Review Response Generator', hotelstack: true, competitor: false },
        { name: 'Smart Upsell Recommendations', hotelstack: true, competitor: false },
        { name: 'Guest Sentiment Analysis', hotelstack: true, competitor: false },
        { name: 'Personalised Stay Recommendations', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'AI Operations',
      features: [
        { name: 'Automated Night Audit', hotelstack: true, competitor: false },
        { name: 'Smart Housekeeping Scheduler', hotelstack: true, competitor: false },
        { name: 'AI Staff Scheduling', hotelstack: true, competitor: false },
        { name: 'Predictive Maintenance', hotelstack: true, competitor: false },
        { name: 'AI Energy Optimisation', hotelstack: true, competitor: false },
        { name: 'AI Operations Agent', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Core PMS & Operations',
      features: [
        { name: 'Property Management System', hotelstack: true, competitor: false },
        { name: 'Channel Manager', hotelstack: true, competitor: false },
        { name: 'Booking Engine', hotelstack: true, competitor: false },
        { name: 'F&B / POS Management', hotelstack: true, competitor: false },
        { name: 'Housekeeping Module', hotelstack: true, competitor: false },
        { name: 'Guest CRM & Loyalty', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Enterprise & Analytics',
      features: [
        { name: 'Digital Twin Dashboard', hotelstack: true, competitor: false },
        { name: 'Cross-Property Intelligence', hotelstack: true, competitor: true },
        { name: 'AI Compliance Automation', hotelstack: true, competitor: false },
        { name: 'Platform Anomaly Detection', hotelstack: true, competitor: false },
        { name: 'AI Travel Agent MCP', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$300+',
    hotelstackNote: 'All 40+ modules with 25 AI features. Revenue management + full PMS + guest CRM + operations. Free onboarding and migration.',
    competitorNote: 'Revenue management only. Requires separate PMS and channel manager. Per-room pricing increases with property size.',
  },
  advantages: [
    'Complete 40+ module HMS with 25 AI features — vs revenue-management-only Duetto',
    'Starting at $49/mo vs $300-1,500+/mo — dramatically lower total cost of ownership',
    '25 AI features spanning revenue, guest, operations, maintenance, compliance — vs 4-6 revenue-only',
    'No separate PMS, channel manager, or CRM needed — single platform, single vendor',
    'Free 48-hour onboarding vs weeks of integration and setup with Duetto',
    'AI guest concierge, voice AI, and predictive maintenance — not available in Duetto',
    'Digital twin, energy optimisation, and AI compliance — unique to HotelStack',
    'Serves SMB to enterprise — Duetto primarily targets large chains and casino resorts',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'Duetto excels at revenue management with its open pricing model, particularly for large chains and casino resorts. HotelStack.io matches Duetto on AI pricing intelligence while adding 20 more AI features, a full 40+ module PMS, and everything else hotels need — all starting at a fraction of Duetto\'s price. For most properties, HotelStack replaces Duetto plus your PMS in one platform.',
    bestFor: [
      'Hotels wanting Duetto-level pricing AI plus a full PMS in one system',
      'Properties that find Duetto\'s $300+/mo too expensive for revenue management alone',
      'Hotels needing AI across all departments, not just revenue',
      'SMB and mid-scale hotels not served by Duetto\'s enterprise focus',
      'Properties wanting to consolidate vendors — one platform instead of PMS + RMS + CRM',
    ],
  },
  faqItems: [
    { question: 'How does HotelStack AI pricing compare to Duetto?', answer: 'HotelStack AI dynamic pricing uses ML models that adjust rates every 15 minutes. Both platforms offer demand forecasting, competitor monitoring, and flexible pricing strategies. The key difference: HotelStack includes this as part of a 40+ module platform with 25 AI features, while Duetto charges $300+/month for revenue management alone.' },
    { question: 'Does HotelStack support open pricing like Duetto?', answer: 'Yes. HotelStack AI dynamic pricing supports rate-by-room-type, day-of-week, length-of-stay, and channel-specific pricing — achieving the same flexibility as Duetto\'s open pricing model. Plus, autonomous rate management enables hands-free 24/7 pricing within defined guardrails.' },
    { question: 'Can HotelStack replace both my PMS and Duetto?', answer: 'Yes. HotelStack is a complete 40+ module hotel management platform with 25 built-in AI features. It replaces your PMS, revenue management system (Duetto), channel manager, guest CRM, and more — all in one platform starting at $49/month.' },
    { question: 'Is Duetto better for casino resorts?', answer: 'Duetto has specific features for casino resort revenue management. HotelStack serves the broader hospitality market with its 40+ module platform. For casino-specific yield management, Duetto may have an edge. For everything else — AI guest experience, operations, maintenance, compliance — HotelStack offers far more.' },
  ],
};

export const COMPARISON_CONFIGS_P4: Record<string, ComparisonConfig> = {
  'hotelstack-vs-ideas': HOTELSTACK_VS_IDEAS,
  'hotelstack-vs-duetto': HOTELSTACK_VS_DUETTO,
};
